// External
import React from 'react';
import { Divider } from '@paypalcorp/pp-react-divider-weasley-v7';

// Internal
import {
  ExpandedValuePropsContainer,
  ExpandedValuePropsRowThreeIcons,
  ExpandedValuePropsRowV2,
  ExpandedValuePropsRowV3,
  ExpandedValuePropsText,
  StretchRewardsContentContainer,
  StretchRewardsHeader,
  StretchRewardsOfferDisclaimer,
  StretchRewardsText,
} from '@/components/CheckoutIncentives/StyledComponents';
import { features, getContent, useMountLogging, useOnMount } from '@/lib';
import { useStoreState } from '@/store';
import { BlueCheckmark, GreyTrophy } from '@/components/Common';
import { logStretchRewardsEligibilityToFpti } from '@/components/Form/fpti';

type StretchRewardsParams = {
  toggleComponent?: JSX.Element; // Optional, as we use this component for forced signup as well.
};

const StretchRewards = ({
  toggleComponent,
}: StretchRewardsParams): JSX.Element => {
  useMountLogging('StretchRewards');
  useOnMount(logStretchRewardsEligibilityToFpti);

  const isExpandedStretchRewards = features.isActive(
    'isExpandedStretchRewards',
  );

  const isOptionalSignup = features.isActive('isOptionalSignup');
  const isForcedSignup = features.isActive('isForcedSignUp');
  const onboardValue = useStoreState(({ form }) => form.onboardOption?.value);
  // Default to forced signup content.
  let stretchRewardsContentKey =
    'checkoutIncentives.stretchRewards.forced.signup.description';
  // User is optional signup
  const isGuestToggle = Boolean(onboardValue === 'guest');
  const isSignupToggle = Boolean(onboardValue === 'signup');

  if (isOptionalSignup && isExpandedStretchRewards) {
    // Optional. Swap content based on state of onboard toggle switch.
    stretchRewardsContentKey = isGuestToggle
      ? 'checkoutIncentives.stretchRewards.optional.guest.description'
      : 'checkoutIncentives.stretchRewards.optional.signup.description';
  }

  const showContentForOptional =
    isOptionalSignup && isSignupToggle && isExpandedStretchRewards;

  return (
    <ExpandedValuePropsContainer data-testid="stretch-rewards">
      <StretchRewardsHeader>
        <StretchRewardsContentContainer>
          <StretchRewardsText data-testid="stretch-rewards-text">
            {getContent(
              stretchRewardsContentKey,
              // Hard coded for now, dynamic in the future.
              { dollarAmount: '$20.00', pointAmount: '(2,000 points)' },
              { useHTML: true },
            )}
          </StretchRewardsText>
        </StretchRewardsContentContainer>
        {isOptionalSignup && toggleComponent}
      </StretchRewardsHeader>
      {(isForcedSignup || showContentForOptional) && (
        <ExpandedValuePropsRowV2>
          <div
            data-testid="stretch-rewards-row"
            className="flex"
            style={{ width: '100%' }}
          >
            <Divider style={{ marginBottom: 16, marginTop: 16 }} />
            <ExpandedValuePropsRowV3>
              <ExpandedValuePropsText>
                {getContent(
                  'checkoutIncentives.stretchRewards.offerInstruction',
                  { transactionCount: '2' },
                )}
              </ExpandedValuePropsText>
              <ExpandedValuePropsRowThreeIcons>
                <BlueCheckmark />
                <GreyTrophy />
              </ExpandedValuePropsRowThreeIcons>
              <StretchRewardsOfferDisclaimer>
                {getContent(
                  'checkoutIncentives.stretchRewards.offerExpiration',
                  {},
                  { useHTML: true },
                )}
              </StretchRewardsOfferDisclaimer>
            </ExpandedValuePropsRowV3>
          </div>
        </ExpandedValuePropsRowV2>
      )}
    </ExpandedValuePropsContainer>
  );
};

export { StretchRewards };
